import {Component, computed, inject} from '@angular/core';
import {Router} from '@angular/router';
import {toSignal} from '@angular/core/rxjs-interop';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {ModuleFunction} from '@frontend/directus';
import {CountryService} from '@frontend/shared';
import {HrToolService} from '../hr-tool.service';
import {ExtendedHrTool} from '../hr-tool.types';
import {SafeHtmlComponent} from '../../../shared/components/safe-html-output/safe-html-output.component';
import {APP_ROUTES} from '../../../app.routes';
import {HrToolAiCapabilityComparisonComponent} from './hr-tool-ai-capability-comparison/hr-tool-ai-capability-comparison.component';

interface ComparisonItem {
  key: string;
  label: string;
  flagUrl?: string | null;
}

interface ComparisonSection {
  title: string;
  items: ComparisonItem[];
  has(tool: ExtendedHrTool, key: string): boolean;
}

/** Full-page side-by-side comparison (base44 Compare): product header cards + ✓/✗ attribute tables. */
@Component({
  selector: 'app-hr-tool-comparison',
  imports: [TranslocoPipe, SafeHtmlComponent, HrToolAiCapabilityComparisonComponent],
  templateUrl: './hr-tool-comparison.component.html',
})
export class HrToolComparisonComponent {
  private readonly hrToolsService = inject(HrToolService);
  private readonly countryService = inject(CountryService);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);

  constructor() {
    this.hrToolsService.ensureArchitecturalPrinciplesLoaded();
  }

  protected readonly activeLanguage = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  protected readonly tools = computed<ExtendedHrTool[]>(
    () => this.hrToolsService.comparisonHrTools().slice(0, 3) as ExtendedHrTool[],
  );

  /** Comparison tables (rows = union of an attribute across the tools, cells = ✓/✗ per tool). */
  protected readonly sections = computed<ComparisonSection[]>(() => {
    const all: ComparisonSection[] = [
      {
        title: 'common.functions',
        items: this.functionItems(),
        has: (tool: ExtendedHrTool, key: string) =>
          (tool.moduleFunctions ?? []).some((entry) => String(entry.id) === key),
      },
      {
        title: 'hr-tool.service-model',
        items: this.serviceModelItems(),
        has: (tool: ExtendedHrTool, key: string) =>
          (tool.serviceModels ?? []).some((entry) => entry.id === key),
      },
      {
        title: 'hrTool.availableLanguages',
        items: this.languageItems(),
        has: (tool: ExtendedHrTool, key: string) =>
          (tool.availableLanguages ?? []).some((entry) => entry.isoCode === key),
      },
      {
        title: 'hrTool.availableCountries',
        items: this.countryItems(),
        has: (tool: ExtendedHrTool, key: string) =>
          (tool.availableCountries ?? []).some((entry) => entry.isoCode === key),
      },
    ];
    return all.filter((section) => section.items.length > 0);
  });

  private readonly functionItems = computed<ComparisonItem[]>(() => {
    const lang = this.activeLanguage();
    const byKey = new Map<string, string>();
    this.tools().forEach((tool) =>
      (tool.moduleFunctions ?? []).forEach((entry) => {
        byKey.set(String(entry.id), this.functionLabel(entry, lang));
      }),
    );
    return this.toSortedItems(byKey);
  });

  private readonly serviceModelItems = computed<ComparisonItem[]>(() => {
    const byKey = new Map<string, string>();
    this.tools().forEach((tool) =>
      (tool.serviceModels ?? []).forEach((entry) => {
        const label = entry.translationKey ? this.transloco.translate(entry.translationKey) : '';
        byKey.set(entry.id, label || entry.name);
      }),
    );
    return this.toSortedItems(byKey);
  });

  private readonly languageItems = computed<ComparisonItem[]>(() => {
    const lang = this.activeLanguage();
    const byKey = new Map<string, string>();
    this.tools().forEach((tool) =>
      (tool.availableLanguages ?? []).forEach((entry) => {
        const label = entry.translations?.find((t) => t.language === lang)?.name ?? entry.isoCode;
        byKey.set(entry.isoCode, label);
      }),
    );
    return this.toSortedItems(byKey);
  });

  private readonly countryItems = computed<ComparisonItem[]>(() => {
    const lang = this.activeLanguage();
    const byKey = new Map<string, ComparisonItem>();
    this.tools().forEach((tool) =>
      (tool.availableCountries ?? []).forEach((entry) => {
        byKey.set(entry.isoCode, {
          key: entry.isoCode,
          label: this.countryService.getCountryDisplayName(entry, lang),
          flagUrl: this.countryService.getCountryFlagUrl(entry.isoCode),
        });
      }),
    );
    return Array.from(byKey.values()).sort((first, second) =>
      first.label.localeCompare(second.label),
    );
  });

  protected applicationTypeLabel(tool: ExtendedHrTool): string {
    return this.hrToolsService.applicationTypeLabel(this.hrToolsService.applicationTypeOf(tool));
  }

  protected toolName(tool: ExtendedHrTool): string {
    return tool.translatedName || tool.name;
  }

  protected description(tool: ExtendedHrTool): string | null {
    if (tool.translatedDescription) {
      return tool.translatedDescription;
    }
    return (
      tool.translations?.find((translation) => translation.language === this.activeLanguage())
        ?.description ?? null
    );
  }

  protected remove(id: string): void {
    this.hrToolsService.removeToolFromComparison(id);
    if (this.tools().length === 0) {
      this.back();
    }
  }

  protected clearAll(): void {
    this.hrToolsService.clearAllComparisonTools();
    this.back();
  }

  protected back(): void {
    void this.router.navigate([APP_ROUTES.HR_TOOLS]);
  }

  protected hideBrokenImage(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }

  private toSortedItems(byKey: Map<string, string>): ComparisonItem[] {
    return Array.from(byKey, ([key, label]) => ({key, label})).sort((first, second) =>
      first.label.localeCompare(second.label),
    );
  }

  private functionLabel(moduleFunction: ModuleFunction, lang: string): string {
    return (
      moduleFunction.translations?.find((translation) => translation.language === lang)?.name ??
      moduleFunction.translations?.[0]?.name ??
      moduleFunction.name
    );
  }
}
